// Lenis と ScrollTrigger を使ったスクロール関連機能。
//
// ============================================
// HTML 構造例:
// ============================================
// <a href="#section">アンカーリンク</a>
// <div class="js-inview">入場アニメーション対象</div>
// ============================================
// CSS 注意:
// is-active 等の表示・トランジションは CSS 側で定義する。

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { bindOnce } from '../utils/bindOnce';

// 挙動を変えたいときはここだけ書き換える
const CONFIG = {
  lenis_duration: 1.4,                                              // Lenis スムーススクロールの duration
  lenis_smoothWheel: true,                                          // ホイールのスムーススクロール
  lenis_syncTouch: false,                                         // false = OS ネイティブのタッチスクロールを使う
  lenis_wheelMultiplier: 0.9,                                       // ホイール1ノッチあたりのスクロール量の倍率
  anchor_duration: 1.2,                                             // アンカーリンクのスクロール時間
  anchor_lerp: 0.2,                                                 // アンカーリンクの lerp 値
  anchorLinkSelector: 'a[href^="#"]:not(.-no-smooth, .l-header__skip-content)', // スムーススクロール対象のアンカー
  inviewSelector: '.js-inview',                                     // 入場アニメーション対象
  inview_start: 'top 75%',                                          // 要素の上端が画面の 75% ラインを越えたら発火
  activeClass: 'is-active',                                         // 入場アニメーション完了時のクラス
};

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const lenis = new Lenis({
  duration: CONFIG.lenis_duration,
  smoothWheel: CONFIG.lenis_smoothWheel && !prefersReducedMotion,
  syncTouch: CONFIG.lenis_syncTouch,
  wheelMultiplier: CONFIG.lenis_wheelMultiplier,
});

lenis.on('scroll', ScrollTrigger.update);

const _raf = (time) => lenis.raf(time * 1000);
gsap.ticker.add(_raf);

gsap.ticker.lagSmoothing(0);
window.addEventListener('pageshow', (e) => {
  if (e.persisted) ScrollTrigger.refresh();
});

// 着地位置の基準は CSS の scroll-padding-top（= ヘッダー高さ + 余白）に揃える。
// PC/SP でヘッダー高さが変わっても追従するよう、実測を優先する。
function getScrollOffset() {
  const rootStyles = getComputedStyle(document.documentElement);
  const header = document.querySelector('.js-header');
  const headerH = header?.getBoundingClientRect().height
    || Number.parseFloat(rootStyles.getPropertyValue('--size-header-h'))
    || 0;
  const fromPadding = Number.parseFloat(rootStyles.scrollPaddingTop);

  // scroll-padding-top がヘッダー実高以上ならそれを使う（余白込みの正規値）
  if (Number.isFinite(fromPadding) && fromPadding >= headerH && fromPadding > 0) {
    return fromPadding;
  }

  // フォールバック: ヘッダー実高 + 余白 20px（:root の scroll-padding-top と同じ）
  return (headerH || 80) + 20;
}

// ページ内アンカーリンクへのスムーススクロール。
// .-no-smooth（無効化用）と .l-header__skip-content（スキップリンク）は対象外。
function lenisPageScroll() {
  const links = document.querySelectorAll(CONFIG.anchorLinkSelector);
  if (links.length === 0) return;

  const scrollToAnchor = (target) => {
    if (prefersReducedMotion) {
      const el = target === 'body' ? document.body : document.querySelector(target);
      el?.scrollIntoView();
      return;
    }
    lenis.scrollTo(target, {
      offset: -getScrollOffset(),
      lerp: CONFIG.anchor_lerp,
      duration: CONFIG.anchor_duration,
    });
  };

  links.forEach((el) => {
    if (!bindOnce(el, 'smoothScroll')) return;

    el.addEventListener('click', (e) => {
      const targetSelector = el.getAttribute('href');

      if (!targetSelector || targetSelector === '#') {
        e.preventDefault();
        scrollToAnchor('body');
        return;
      }

      try {
        if (!document.querySelector(targetSelector)) return;
      } catch {
        return;
      }

      e.preventDefault();
      scrollToAnchor(targetSelector);
    });
  });
}

// 別ページから /path/#anchor で遷移してきたときの着地位置を合わせる。
// ブラウザは .js-inview の入場アニメーション（translate 28px）を含んだ位置でジャンプするため、
// 何もしないとアニメーション完了後に着地先がヘッダーの下へ潜り込む。
function alignHashTarget() {
  const hash = window.location.hash;

  if (hash.length < 2) return;

  let target = null;

  try {
    target = document.querySelector(hash);
  } catch {
    return;
  }

  if (!target) return;

  // 直リンクの着地先は入場アニメーションを見せず、最初から表示済みの状態にする
  if (target.classList.contains(CONFIG.inviewSelector.slice(1))) {
    target.style.transition = 'none';
    target.classList.add(CONFIG.activeClass);
  }

  // rect の読み取りでスタイルが反映されるため、ここでは translate が外れた位置が取れる
  const top = target.getBoundingClientRect().top + window.scrollY;

  lenis.scrollTo(top - getScrollOffset(), { immediate: true });

  requestAnimationFrame(() => {
    target.style.transition = '';
  });
}

// .js-inview が画面内に入ると is-active を付与する。トランジションは CSS 側で定義する。
function inViewAnm() {
  if (prefersReducedMotion) {
    document.querySelectorAll(CONFIG.inviewSelector).forEach((el) => el.classList.add(CONFIG.activeClass));
    return;
  }

  ScrollTrigger.batch(CONFIG.inviewSelector, {
    start: CONFIG.inview_start,
    once: true,
    onEnter: (batch) => {
      batch.forEach((el) => el.classList.add(CONFIG.activeClass));
    },
  });
}

export { lenisPageScroll, alignHashTarget, inViewAnm, getScrollOffset };
